import { formatCurrency, formatCurrencyFull } from 'utilities/currency'

import {
  Attribute,
  Currency,
  MessageBox,
  Space,
  Typography,
  ValidationMessage
} from '../../../../../components/core-v2'
import { TradeApprove } from '../../../../../components/core-v2/molecules/trade-approve'
import TradeApproveReadonly from '../../../../../components/core-v2/molecules/trade-approve-readonly'
import { SettlementTransferTable } from '../../../../../components/core-v2/organisms/settlement-transfer-table'
import TradeEventDisplay from '../../../../../components/core-v2/organisms/trade-event-display/trade-event-display'
import { round } from '../../../../tradebuilder-v2/util'
import { TradeSettlementActions, TradeSettlementStepState } from '../state/trade-settlement-slice'

export type TradeSettlementStatelessProps = {
  actions: TradeSettlementActions
  onSettlementApprove: () => void
  onSettlementReject: () => void
  state: TradeSettlementStepState
}

type userActionType = 'client-action' | 'absa-cash-approver' | 'absa-transfer-approver' | 'none'

const TradeSettlementStateless = ({
  actions,
  onSettlementApprove,
  onSettlementReject,
  state
}: TradeSettlementStatelessProps) => {
  let userActionType = null as userActionType
  if (state.userType === 'business-unit' || state.userType === 'client') {
    userActionType = 'client-action'
  } else if (
    state.userType === 'admin' ||
    (state.userType === 'absa' && state.userRoles.includes('Operations'))
  ) {
    userActionType = 'absa-cash-approver'
  } else if (state.userType === 'absa' && state.userRoles.includes('BankAdmin')) {
    userActionType = 'absa-transfer-approver'
  } else {
    userActionType = 'none'
  }

  const renderDetails = () => (
    <Space mt={6}>
      <MessageBox data-private state="success">
        <Attribute name="Currency">
          <Typography variant="b3">{state.currency}</Typography>
        </Attribute>
        <Attribute name="Amount">
          <Typography variant="b3">{formatCurrency(state.investment, state.currency)}</Typography>
        </Attribute>
        <Attribute name="Main Correspondent (Nostro Bank)">
          <Typography variant="b3">Absa Bank Limited - Derivative</Typography>
        </Attribute>
        <Attribute name="Nostro Agent BIC (F57A)">
          <Typography variant="b3">ABSAZAJJXXX</Typography>
        </Attribute>
        <Attribute name="Beneficiary Bank">
          <Typography variant="b3">Absa Bank Limited</Typography>
        </Attribute>
        <Attribute name="Beneficiary Bank BIC (F57A)">
          <Typography variant="b3">ABSAZAJJXXX</Typography>
        </Attribute>
        <Attribute name="IBAN/Account Number/Clearing Code">
          <Typography variant="b3">660158642</Typography>
        </Attribute>
      </MessageBox>
    </Space>
  )

  const renderCompletedState = () => (
    <Space>
      {state.approvalEvents.map(approvalEvent => (
        <TradeEventDisplay key={approvalEvent.userId} {...approvalEvent} />
      ))}
    </Space>
  )

  const renderAdminPendingState = () => (
    <>
      <Space my={6}>
        <MessageBox state="success">
          <Space mb={4}>
            <Typography variant="b3">Check the account has been settled</Typography>
          </Space>
          <Typography variant="b1">
            {formatCurrencyFull(state.investment, state.currency)}
          </Typography>
        </MessageBox>
      </Space>
      <Space mt={5}>
        <ValidationMessage errors={state.errors} />
      </Space>
      <TradeApprove
        canApprove
        allowComments={false}
        approveBtnCaption="Approve"
        approvingSection="settlement"
        userName={state.userName}
        onApprove={onSettlementApprove}
        onReject={onSettlementReject}
      />
    </>
  )

  const renderTransferPendingState = () => (
    <>
      <Space my={6}>
        <SettlementTransferTable
          assets={state.assetsToTransfer}
          onChangeAssetUnits={(assetId, units) => actions.setAssetUnits({ assetId, units })}
          onChangePurchasePrice={(assetId, price) =>
            actions.setOriginalPurchasePrice({ assetId, price })
          }
        />
      </Space>
      <Space mt={5}>
        <ValidationMessage errors={state.errors} />
      </Space>
      <Space mt={5}>
        <Attribute name="Base portfolio value">
          <Currency code="ZAR" value={state.basePortfolioValue} />
        </Attribute>
        <Attribute name="Current portfolio value">
          <Currency code="ZAR" value={state.currentPortfolioValue} />
        </Attribute>
        <Attribute name="Note units">{round(state.noteUnits, 2)}</Attribute>
        <Attribute name="Base cost per unit">
          <Currency code="ZAR" value={state.baseCostPerUnit} />
        </Attribute>
        <Attribute name="Note price">
          <Currency code="ZAR" value={state.notePrice} />
        </Attribute>
      </Space>
      <TradeApprove
        canApprove
        allowComments={false}
        approveBtnCaption="Approve"
        approvingSection="settlement"
        userName={state.userName}
        onApprove={onSettlementApprove}
        onReject={onSettlementReject}
      />
    </>
  )

  const renderReadonlyState = () => (
    <Space my={6}>
      <TradeApproveReadonly waitingDescription="This trade is awaiting settlement. Please ensure the trade is settled and check back later" />
    </Space>
  )

  const renderReadonlyStateWithDetails = () => (
    <>
      {renderDetails()}
      {renderReadonlyState()}
    </>
  )

  return (
    <>
      {!state.hasCompleted &&
        userActionType === 'absa-cash-approver' &&
        !state.isTransfer &&
        renderAdminPendingState()}
      {!state.hasCompleted &&
        userActionType === 'client-action' &&
        !state.isTransfer &&
        renderReadonlyStateWithDetails()}

      {!state.hasCompleted &&
        (userActionType === 'absa-cash-approver' || userActionType === 'absa-transfer-approver') &&
        state.isTransfer &&
        renderTransferPendingState()}
      {!state.hasCompleted &&
        userActionType === 'client-action' &&
        state.isTransfer &&
        renderReadonlyState()}

      {!state.hasCompleted && userActionType === 'none' && renderReadonlyState()}

      {state.hasCompleted && renderCompletedState()}
    </>
  )
}

export default TradeSettlementStateless
